import { useEffect, useState } from 'react';
import axios from 'axios';
import { ChevronDown, ChevronRight, MonitorSmartphone, Plus, Trash2, Pencil, Lock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import EppKafkaCredentials from './EppKafkaCredentials';

// Console Sangfor Athena EPP che inviano syslog attraverso un broker (backend/src/routes/eppManagers.ts,
// ingestion/eppRouting.ts), nella pagina Sites & Brokers del tenant accanto alle altre integrazioni
// EDR (29/10/2026). Visibile a tutti; gli admin registrano console che restano nel tenant del loro
// broker; solo i platform admin configurano il "relay" verso un altro tenant (sposta eventi fra
// tenant) e vedono le console arrivate su broker di altri tenant. Una console non registrata resta nel tenant del
// broker senza inventario; "dedicated" = console di un solo cliente (tutto al suo tenant: se il
// tenant è diverso da quello del broker, servono TCP e l'IP del manager); "shared" = console
// multi-cliente, resta nel tenant del broker senza inventario.

interface Registered {
  id: number; broker_id: number; broker_name: string; broker_tenant_id: number; broker_tenant_name: string;
  mgr_id: string; mode: 'dedicated' | 'shared'; tenant_id: number; tenant_name: string;
  allowed_sender_ips: string | null; require_tcp: boolean; label: string | null; enabled: boolean; senders: Sender[];
  objects: number; received: number; last_seen_at: string | null; unverified: number; unverified_last_seen_at: string | null;
}
interface Discovered {
  broker_id: number; broker_name: string; broker_tenant_id: number; broker_tenant_name: string; mgr_id: string;
  objects: number; received: number; last_seen_at: string; log_types: string[]; on_tenant_broker: boolean; senders: Sender[];
}
// Da dove arriva la console (backend ingestion/eppRouting.ts recordEppSender, dal 30/10/2026).
interface Sender { ip: string; transport: string; name: string | null; last_seen_at: string }

function Senders({ senders, allowed }: { senders: Sender[]; allowed?: string | null }) {
  if (!senders?.length) return <span className="text-gray-300">—</span>;
  const allow = allowed ? allowed.split(/[\s,;]+/).filter(Boolean) : null;
  return (
    <span className="flex flex-col gap-0.5">
      {senders.map(x => {
        // Solo con IP singoli: con un intervallo CIDR nella lista non si giudica qui (lo fa il backend).
        const outside = allow && !allow.some(a => a.includes('/')) && !allow.includes(x.ip);
        return (
          <span key={`${x.ip}|${x.transport}`} className="font-mono text-xs whitespace-nowrap" title={`last line ${new Date(x.last_seen_at).toLocaleString()}`}>
            {x.name && <span className="font-sans font-medium text-gray-800 mr-1">{x.name}</span>}{x.ip || 'unknown'}{' '}
            <span className={`badge ${x.transport === 'tcp' ? 'bg-gray-100 text-gray-600' : 'bg-amber-50 text-amber-700'}`}>{x.transport}</span>
            {outside && <span className="badge ml-1 bg-red-50 text-red-700" title="This IP is not in the allowed sender IPs: its lines stay in the broker tenant">not allowed</span>}
          </span>
        );
      })}
    </span>
  );
}
interface Tenant { id: number; name: string }

interface FormState {
  id: number | null; brokerId: number; brokerLabel: string; brokerTenantId: number; mgrId: string;
  mode: 'dedicated' | 'shared'; tenantId: number; ips: string; requireTcp: boolean; label: string;
}

export default function SangforEppConsolesPanel({ tenantId }: { tenantId: number }) {
  const { isAdmin } = useAuth();
  const [canRelay, setCanRelay] = useState(false);
  const [open, setOpen] = useState(false);
  const [registered, setRegistered] = useState<Registered[]>([]);
  const [discovered, setDiscovered] = useState<Discovered[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [form, setForm] = useState<FormState | null>(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showOtherBrokers, setShowOtherBrokers] = useState(false);

  function load() {
    axios.get('/api/epp-managers', { params: { tenantId } }).then(res => {
      setRegistered(res.data.data.registered);
      setDiscovered(res.data.data.discovered);
      setCanRelay(res.data.data.canRelay === true);
    }).catch(() => undefined);
  }

  useEffect(() => {
    setForm(null);
    if (!open) return;
    load();
  }, [open, tenantId]);

  // L'elenco dei tenant serve solo a chi può scegliere una destinazione diversa (platform admin).
  useEffect(() => {
    if (open && canRelay && tenants.length === 0) axios.get('/api/tenants').then(res => setTenants(res.data.data)).catch(() => undefined);
  }, [open, canRelay, tenants.length]);

  // Prima le console viste dai broker di questo tenant; le altre (es. la console del cliente che
  // arriva dal broker cloud dell'MSSP) su richiesta.
  const discoveredShown = discovered.filter(d => d.on_tenant_broker || showOtherBrokers);
  const otherCount = discovered.filter(d => !d.on_tenant_broker).length;

  function startFromDiscovered(d: Discovered) {
    setError('');
    setForm({ id: null, brokerId: d.broker_id, brokerLabel: `${d.broker_name} (${d.broker_tenant_name})`, brokerTenantId: d.broker_tenant_id,
      mgrId: d.mgr_id, mode: 'dedicated', tenantId, ips: [...new Set((d.senders || []).filter(x => x.transport === 'tcp' && x.ip).map(x => x.ip))].join(', '), requireTcp: true,
      label: (d.senders || []).find(x => x.name)?.name || '' });
  }

  function startEdit(r: Registered) {
    setError('');
    setForm({ id: r.id, brokerId: r.broker_id, brokerLabel: `${r.broker_name} (${r.broker_tenant_name})`, brokerTenantId: r.broker_tenant_id,
      mgrId: r.mgr_id, mode: r.mode, tenantId: r.tenant_id, ips: r.allowed_sender_ips || '', requireTcp: r.require_tcp, label: r.label || '' });
  }

  async function save() {
    if (!form) return;
    setSaving(true);
    setError('');
    const body = { brokerId: form.brokerId, mgrId: form.mgrId, mode: form.mode, tenantId: form.tenantId,
      allowedSenderIps: form.ips.trim() || null, requireTcp: form.requireTcp, label: form.label.trim() || null };
    try {
      if (form.id) await axios.put(`/api/epp-managers/${form.id}`, body);
      else await axios.post('/api/epp-managers', body);
      setForm(null);
      load();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error while saving');
    } finally {
      setSaving(false);
    }
  }

  async function toggle(r: Registered) {
    await axios.put(`/api/epp-managers/${r.id}`, { mode: r.mode, tenantId: r.tenant_id, allowedSenderIps: r.allowed_sender_ips,
      requireTcp: r.require_tcp, label: r.label, enabled: !r.enabled }).catch(() => undefined);
    load();
  }

  async function remove(r: Registered) {
    if (!confirm(`Remove the registration of console ${r.mgr_id}? Its events will stay in the broker tenant (${r.broker_tenant_name}), without asset inventory.`)) return;
    await axios.delete(`/api/epp-managers/${r.id}`).catch(() => undefined);
    load();
  }

  const relayed = form && form.mode === 'dedicated' && form.tenantId !== form.brokerTenantId;

  return (
    <div className="card p-5 mb-3">
      <button type="button" onClick={() => setOpen(v => !v)} className={`w-full flex items-center gap-2 text-left ${open ? 'mb-1' : ''}`}>
        {open ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
        <MonitorSmartphone size={18} className="text-primary-600" />
        <h2 className="font-medium text-gray-900">Sangfor Athena EPP consoles</h2>
        {registered.length > 0 && <span className="badge ml-auto bg-green-100 text-green-700">{registered.length} registered</span>}
      </button>
      {open && (
        <div className="mt-2 space-y-4">
          <p className="text-xs text-gray-500">
            Endpoint Secure managers that send syslog (Data Sync → Syslog Reporting, <b>All Info</b>) through a broker.
            An unregistered console stays in the broker's tenant, without asset inventory. <b>Dedicated</b> = console of a single
            customer: everything goes to that tenant; if it is not the broker's tenant, the manager must send over <b>TCP</b> and from
            the IP(s) below. <b>Shared</b> = console with several customers: it stays in the broker's tenant without inventory
            (detections do not carry the group, so they cannot be split by customer).
          </p>

          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">Registered</h3>
            {registered.length === 0 ? <p className="text-xs text-gray-400">No console registered.</p> : (
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-gray-500">
                  <th className="py-1">Console (mgr_id)</th><th>Via broker</th><th>Mode</th><th>Destination</th><th>Sender IPs</th><th>Sends from</th><th>Distinct events</th><th>Last seen</th><th></th>
                </tr></thead>
                <tbody>
                  {registered.map(r => (
                    <tr key={r.id} className={`border-t border-gray-100 ${r.enabled ? '' : 'opacity-50'}`}>
                      <td className="py-1.5"><span className="font-mono">{r.mgr_id}</span>{r.label && <span className="text-gray-500"> — {r.label}</span>}</td>
                      <td className="text-gray-600">{r.broker_name} <span className="text-gray-400">({r.broker_tenant_name})</span></td>
                      <td>{r.mode === 'shared' ? <span className="badge bg-gray-100 text-gray-600">shared</span> : <span className="badge bg-primary-50 text-primary-700">dedicated</span>}</td>
                      <td className="text-gray-700">{r.tenant_name}{r.tenant_id !== r.broker_tenant_id && <span className="badge ml-1 bg-amber-50 text-amber-700" title="Relayed through another tenant's broker">relay</span>}</td>
                      <td className="font-mono text-xs text-gray-600">{r.allowed_sender_ips || '—'}{r.require_tcp && r.tenant_id !== r.broker_tenant_id && <Lock size={11} className="inline ml-1 text-gray-400" aria-label="TCP required" />}</td>
                      <td><Senders senders={r.senders} allowed={r.tenant_id !== r.broker_tenant_id ? r.allowed_sender_ips : null} /></td>
                      <td className="text-gray-600" title={`${r.received} received including resends`}>
                        {r.objects}
                        {r.unverified > 0 && (
                          <span className="badge ml-1 bg-red-50 text-red-700"
                            title={`${r.unverified} event(s) arrived from a sender IP or transport not allowed for this console (last ${r.unverified_last_seen_at ? new Date(r.unverified_last_seen_at).toLocaleString() : '—'}): they stayed in ${r.broker_tenant_name}`}>
                            +{r.unverified} not routed
                          </span>
                        )}
                      </td>
                      <td className="text-xs text-gray-500">{r.last_seen_at ? new Date(r.last_seen_at).toLocaleString() : <span className="text-amber-700">nothing yet</span>}</td>
                      <td className="text-right whitespace-nowrap">
                        {isAdmin && (canRelay || r.tenant_id === r.broker_tenant_id) && (<>
                        <button onClick={() => toggle(r)} className="text-xs text-gray-500 hover:text-gray-800 mr-2">{r.enabled ? 'Disable' : 'Enable'}</button>
                        <button onClick={() => startEdit(r)} className="text-gray-400 hover:text-primary-600 mr-2" title="Edit"><Pencil size={14} /></button>
                        <button onClick={() => remove(r)} className="text-gray-400 hover:text-red-600" title="Remove"><Trash2 size={14} /></button>
                        </>)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">Seen in the logs, not registered</h3>
            {discoveredShown.length === 0 ? <p className="text-xs text-gray-400">None on this tenant's brokers.</p> : (
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-gray-500">
                  <th className="py-1">Console (mgr_id)</th><th>Via broker</th><th>Sends from</th><th>Distinct events</th><th>Log types</th><th>Last seen</th><th></th>
                </tr></thead>
                <tbody>
                  {discoveredShown.map(d => (
                    <tr key={`${d.broker_id}|${d.mgr_id}`} className="border-t border-gray-100">
                      <td className="py-1.5 font-mono">{d.mgr_id}</td>
                      <td className="text-gray-600">{d.broker_name} <span className="text-gray-400">({d.broker_tenant_name})</span></td>
                      <td><Senders senders={d.senders} /></td>
                      <td className="text-gray-600" title={`${d.received} received including resends`}>{d.objects}</td>
                      <td className="text-xs text-gray-500">{d.log_types.join(', ')}</td>
                      <td className="text-xs text-gray-500">{new Date(d.last_seen_at).toLocaleString()}</td>
                      <td className="text-right">
                        {isAdmin && (canRelay || d.broker_tenant_id === tenantId) && (
                          <button onClick={() => startFromDiscovered(d)} className="btn-secondary text-xs inline-flex items-center gap-1"><Plus size={12} /> Register</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {canRelay && otherCount > 0 && (
              <button type="button" onClick={() => setShowOtherBrokers(v => !v)} className="text-xs text-primary-600 hover:underline mt-2">
                {showOtherBrokers ? 'Hide consoles seen on other tenants\' brokers' : `Show ${otherCount} console(s) seen on other tenants' brokers (e.g. relayed through the cloud broker)`}
              </button>
            )}
          </div>

          {isAdmin && canRelay && <EppKafkaCredentials tenantId={tenantId} />}

          {form && (
            <div className="border border-gray-200 rounded-md p-4 space-y-3 bg-gray-50">
              <h3 className="text-sm font-medium text-gray-800">{form.id ? 'Edit' : 'Register'} console <span className="font-mono">{form.mgrId}</span> via {form.brokerLabel}</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Mode</label>
                  <select value={form.mode} onChange={e => setForm({ ...form, mode: e.target.value as FormState['mode'] })} className="input-field w-full">
                    <option value="dedicated">Dedicated (single customer)</option>
                    <option value="shared">Shared (several customers)</option>
                  </select>
                </div>
                {canRelay && <div>
                  <label className="block text-xs text-gray-500 mb-1">Destination tenant</label>
                  <select value={form.mode === 'shared' ? form.brokerTenantId : form.tenantId} disabled={form.mode === 'shared'}
                    onChange={e => setForm({ ...form, tenantId: Number(e.target.value) })} className="input-field w-full">
                    {tenants.map(t => <option key={t.id} value={t.id}>{t.name}{t.id === form.brokerTenantId ? ' (broker tenant)' : ''}</option>)}
                  </select>
                </div>}
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Label</label>
                  <input value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} className="input-field w-full" placeholder="e.g. Customer EPP cloud console" />
                </div>
                {relayed && (
                  <>
                    <div className="md:col-span-2">
                      <label className="block text-xs text-gray-500 mb-1">Manager sender IP(s) as seen by the broker (required)</label>
                      <input value={form.ips} onChange={e => setForm({ ...form, ips: e.target.value })} className="input-field w-full font-mono" placeholder="203.0.113.10" />
                    </div>
                    <label className="flex items-center gap-2 text-sm text-gray-700 mt-5">
                      <input type="checkbox" checked={form.requireTcp} onChange={e => setForm({ ...form, requireTcp: e.target.checked })} />
                      Require TCP
                    </label>
                    <p className="md:col-span-3 text-xs text-amber-700">
                      Events will move from the broker's tenant to another tenant based on the console id written in the logs.
                      They are accepted only from the IP(s) above{form.requireTcp ? ' and over TCP (UDP source addresses can be spoofed)' : ''};
                      the broker must be version 0.1.1 or later to declare the transport. Restrict the broker's syslog port to the manager IP.
                    </p>
                  </>
                )}
              </div>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <div className="flex gap-2">
                <button onClick={save} disabled={saving} className="btn-primary">{saving ? 'Saving…' : 'Save'}</button>
                <button onClick={() => setForm(null)} className="btn-secondary">Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
